/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Shared author/team-member avatar. Renders the uploaded avatar when the
 * profile has one, otherwise a two-letter initials fallback on the house
 * slate disc. Used by the article byline + author box and by the About page
 * team section — one component so the fallback looks identical everywhere.
 */

import React from 'react';
import type { PublicAuthor } from '../types/api/profiles';

// The public author — `toPublicAuthor` in src/lib/publicAuthors.js, which the
// API and the bake both build it with; ONE definition in
// src/types/api/profiles.ts, re-exported under the name the pages import.
export type { PublicAuthor };

export const authorInitials = (name: string): string =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

export const AuthorAvatar: React.FC<{
  author: PublicAuthor;
  sizeClass?: string;
  textClass?: string;
}> = ({ author, sizeClass = 'w-12 h-12', textClass = 'text-sm' }) => (
  <div
    className={`${sizeClass} rounded-full overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 font-black shrink-0 ${textClass}`}
  >
    {author.avatarPath ? (
      <img
        src={author.avatarPath}
        alt={author.displayName}
        className="w-full h-full object-cover"
        loading="lazy"
        decoding="async"
      />
    ) : (
      <span>{authorInitials(author.displayName)}</span>
    )}
  </div>
);
